/**
 * Icon registry aligned with react-native-vector-icons used in mobile app:
 * - MaterialDesignIcons → @mdi/js (Material Community Icons)
 * - MaterialIcons → @mui/icons-material (Google Material Icons)
 * - FontAwesome5 → @fortawesome/free-solid-svg-icons
 */

import * as MdiJs from '@mdi/js'
import * as MuiIcons from '@mui/icons-material'
import * as FaSolid from '@fortawesome/free-solid-svg-icons'
import * as FaRegular from '@fortawesome/free-regular-svg-icons'

import { createMdiWrapper, createFontAwesomeWrapper, createMuiWrapper } from './iconWrappers'
import { IconRegistryItem } from '@microservices/wiskey-react-components'

const russianTranslations: Record<string, string> = {
  home: 'дом',
  user: 'пользователь',
  settings: 'настройки',
  search: 'поиск',
  edit: 'редактировать',
  delete: 'удалить',
  save: 'сохранить',
  add: 'добавить',
  close: 'закрыть',
  plane: 'самолет',
  runway: 'взлетная полоса',
  baggage: 'багаж',
  bus: 'автобус',
  taxi: 'такси',
  terminal: 'терминал',
  cafe: 'кафе',
  restaurant: 'ресторан',
}

const categoryKeywords: Record<string, string[]> = {
  AircraftMaintenance: ['aircraft', 'wrench', 'tools', 'engine', 'maintenance', 'fuel', 'gear'],
  AirsideOperations: ['runway', 'tower', 'arrival', 'departure', 'hangar', 'jetway'],
  PassengerProcessing: ['passport', 'ticket', 'boarding', 'checkin', 'passenger', 'customer'],
  GroundTransport: ['taxi', 'bus', 'shuttle', 'car', 'parking', 'train', 'tram', 'vehicle'],
  TerminalServices: ['terminal', 'info', 'restroom', 'toilet', 'elevator', 'escalator', 'kiosk', 'wifi'],
  SafetySecurity: ['safety', 'security', 'shield', 'lock', 'alert', 'warning', 'fire'],
  NavigationWeather: ['compass', 'navigation', 'map', 'radar', 'weather', 'wind', 'cloud', 'sun'],
  LogisticsSupport: ['baggage', 'luggage', 'cargo', 'belt', 'trolley', 'warehouse', 'container'],
  HospitalityRetail: ['restaurant', 'cafe', 'coffee', 'bar', 'shop', 'store', 'lounge', 'hotel', 'gift'],
  Other: [],
}

function pascalToKebab(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase()
}

function getCategoryFromName(iconName: string): string {
  const lower = iconName.toLowerCase()
  for (const [category, keywords] of Object.entries(categoryKeywords)) {
    if (keywords.some(kw => lower.includes(kw))) return category
  }
  return 'Other'
}

function getRussianTranslation(iconName: string): string {
  const lower = iconName.toLowerCase()
  for (const [en, ru] of Object.entries(russianTranslations)) {
    if (lower.includes(en)) return ru
  }
  return ''
}

// MDI: mdiAccount -> account, mdiAirplaneTakeoff -> airplane-takeoff
function mdiExportNameToKebab(exportName: string): string {
  if (!exportName.startsWith('mdi')) return exportName
  const withoutPrefix = exportName.slice(3)
  return pascalToKebab(withoutPrefix)
}

// FontAwesome: faHome -> home, faAddressBook -> address-book
function faExportNameToKebab(exportName: string): string {
  if (!exportName.startsWith('fa')) return exportName
  const withoutPrefix = exportName.slice(2)
  return pascalToKebab(withoutPrefix)
}

// MUI: Home -> home, AddCircle -> add-circle
function muiExportNameToKebab(exportName: string): string {
  return pascalToKebab(exportName)
}

const MUI_EXCLUDE = new Set([
  'createSvgIcon',
  'default',
  'SvgIcon',
])

function buildIconRegistry(): IconRegistryItem[] {
  const registry: IconRegistryItem[] = []

  // 1. MaterialDesignIcons (MDI) - from @mdi/js
  for (const [exportName, path] of Object.entries(MdiJs)) {
    if (typeof path !== 'string') continue
    if (!exportName.startsWith('mdi')) continue
    const kebabName = mdiExportNameToKebab(exportName)
    registry.push({
      Component: createMdiWrapper(path),
      id: kebabName,
      library: 'MaterialDesignIcons',
      categories: [getCategoryFromName(kebabName)],
      searchTerms: [kebabName, getRussianTranslation(kebabName)].filter(Boolean),
    })
  }

  // 2. MaterialIcons (Google) - from @mui/icons-material
  for (const [exportName, IconComponent] of Object.entries(MuiIcons)) {
    if (typeof IconComponent !== 'function') continue
    if (MUI_EXCLUDE.has(exportName)) continue
    if (exportName.startsWith('_')) continue
    const kebabName = muiExportNameToKebab(exportName)
    registry.push({
      Component: createMuiWrapper(IconComponent as React.ComponentType<any>),
      id: kebabName,
      library: 'MaterialIcons',
      categories: [getCategoryFromName(kebabName)],
      searchTerms: [kebabName, getRussianTranslation(kebabName)].filter(Boolean),
    })
  }

  // 3. FontAwesome5 - from @fortawesome (solid + regular)
  const faProcessed = new Set<string>()
  for (const [exportName, iconDef] of Object.entries(FaSolid)) {
    if (!iconDef || typeof iconDef !== 'object') continue
    if (!exportName.startsWith('fa') || exportName === 'prefix') continue
    const def = iconDef as { prefix?: string; icon?: unknown[] }
    if (!def.icon || !Array.isArray(def.icon)) continue
    const kebabName = faExportNameToKebab(exportName)
    if (faProcessed.has(kebabName)) continue
    faProcessed.add(kebabName)
    registry.push({
      Component: createFontAwesomeWrapper(iconDef as any),
      id: kebabName,
      library: 'FontAwesome5',
      categories: [getCategoryFromName(kebabName)],
      searchTerms: [kebabName, getRussianTranslation(kebabName)].filter(Boolean),
    })
  }
  for (const [exportName, iconDef] of Object.entries(FaRegular)) {
    if (!iconDef || typeof iconDef !== 'object') continue
    if (!exportName.startsWith('fa') || exportName === 'prefix') continue
    const def = iconDef as { prefix?: string; icon?: unknown[] }
    if (!def.icon || !Array.isArray(def.icon)) continue
    const kebabName = faExportNameToKebab(exportName)
    if (faProcessed.has(kebabName)) continue
    faProcessed.add(kebabName)
    registry.push({
      Component: createFontAwesomeWrapper(iconDef as any),
      id: kebabName,
      library: 'FontAwesome5',
      categories: [getCategoryFromName(kebabName)],
      searchTerms: [kebabName, getRussianTranslation(kebabName)].filter(Boolean),
    })
  }

  return registry
}

export const ICON_REGISTRY = buildIconRegistry()
